import { ApiError, extractApiMessage, hasBodyFlag } from '@/lib/api-error'
import { env } from '@/lib/env'

type Scalar = string | number | boolean
export type QueryParams = Record<string, Scalar | Scalar[] | null | undefined>

export interface RequestOptions {
    params?: QueryParams
    signal?: AbortSignal
    /** Opt out of the global 401 handler for probes that expect to fail (e.g. the session check). */
    skipUnauthorizedHandler?: boolean
}

/**
 * Serializes query params to match what the admin API accepts.
 *
 * The duplication of single-element arrays is deliberate and load-bearing, carried
 * over from the legacy client's `toArrayParam`. Express parses `?condition=Near+Mint`
 * as the string `'Near Mint'`, which fails the backend's `z.array(...)` schemas for
 * multi-select filters. Emitting the lone value twice makes it parse as an array;
 * the resulting `$in: ['x', 'x']` is equivalent to `$in: ['x']` server-side.
 */
function buildSearch(params?: QueryParams): string {
    if (!params) return ''
    const search = new URLSearchParams()

    for (const [key, value] of Object.entries(params)) {
        if (value === null || value === undefined || value === '') continue

        if (Array.isArray(value)) {
            if (value.length === 0) continue
            if (value.length === 1) {
                search.append(key, String(value[0]))
                search.append(key, String(value[0]))
                continue
            }
            for (const entry of value) search.append(key, String(entry))
            continue
        }

        search.append(key, String(value))
    }

    const qs = search.toString()
    return qs ? `?${qs}` : ''
}

let onUnauthorized: (() => void) | null = null
let onRequires2faSetup: (() => void) | null = null

/** Registered once by the auth layer so the client stays free of router imports. */
export function setUnauthorizedHandler(handler: (() => void) | null) {
    onUnauthorized = handler
}

/** Registered once by the auth layer so the client stays free of query imports. */
export function setRequires2faSetupHandler(handler: (() => void) | null) {
    onRequires2faSetup = handler
}

function noteAuthFailure(status: number, body: unknown, skipUnauthorizedHandler?: boolean) {
    if (status === 401 && !skipUnauthorizedHandler) onUnauthorized?.()
    if (status === 403 && hasBodyFlag(body, 'requires2faSetup')) onRequires2faSetup?.()
}

async function parseBody(response: Response): Promise<unknown> {
    const contentType = response.headers.get('content-type') ?? ''
    try {
        if (contentType.includes('application/json')) return await response.json()
        const text = await response.text()
        return text.length ? text : null
    } catch {
        return null
    }
}

async function request<T>(
    method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE',
    path: string,
    body?: unknown,
    options: RequestOptions = {}
): Promise<T> {
    const url = `${env.apiUrl}${path}${buildSearch(options.params)}`

    let response: Response
    try {
        response = await fetch(url, {
            method,
            // Every admin endpoint is behind a cross-site session cookie.
            credentials: 'include',
            signal: options.signal,
            headers: body === undefined ? undefined : { 'Content-Type': 'application/json' },
            body: body === undefined ? undefined : JSON.stringify(body)
        })
    } catch (error) {
        // Let cancellations propagate untouched so TanStack Query can discard them
        // rather than rendering them as failures.
        if (error instanceof DOMException && error.name === 'AbortError') throw error
        throw new ApiError({
            status: 0,
            body: null,
            url,
            message: error instanceof Error ? error.message : 'Network request failed'
        })
    }

    const parsed = await parseBody(response)

    if (!response.ok) {
        noteAuthFailure(response.status, parsed, options.skipUnauthorizedHandler)
        throw new ApiError({
            status: response.status,
            body: parsed,
            url,
            message: extractApiMessage(parsed) ?? `Request failed with status ${response.status}`
        })
    }

    return parsed as T
}

export const api = {
    get: <T>(path: string, options?: RequestOptions) => request<T>('GET', path, undefined, options),
    post: <T>(path: string, body?: unknown, options?: RequestOptions) => request<T>('POST', path, body, options),
    put: <T>(path: string, body?: unknown, options?: RequestOptions) => request<T>('PUT', path, body, options),
    patch: <T>(path: string, body?: unknown, options?: RequestOptions) => request<T>('PATCH', path, body, options),
    delete: <T>(path: string, options?: RequestOptions) => request<T>('DELETE', path, undefined, options)
}

/**
 * Fetches a raw Blob (label PDFs, exports streamed by the API). Kept separate
 * because the JSON path above would destroy the body.
 */
export async function apiBlob(path: string, options: RequestOptions = {}): Promise<Blob> {
    const url = `${env.apiUrl}${path}${buildSearch(options.params)}`
    const response = await fetch(url, { credentials: 'include', signal: options.signal })
    if (!response.ok) {
        const body = await parseBody(response)
        noteAuthFailure(response.status, body, options.skipUnauthorizedHandler)
        throw new ApiError({
            status: response.status,
            body,
            url,
            message: `Request failed with status ${response.status}`
        })
    }
    return response.blob()
}
